import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided-tour steps for the two pages added by the policy-UI batch.
 *
 * Both live here rather than beside their pages, following `risks-tour-steps.ts`: the registry
 * imports every step set, and keeping them in `pages/` keeps that import list flat.
 *
 * **Every `anchor` is the value of a `data-tour-step` ATTRIBUTE on a real element, never a CSS
 * selector.** A selector couples a tour to class names a refactor is free to change without anyone
 * noticing the tour broke. A step whose anchor is absent from the DOM is SKIPPED rather than
 * rendered pointing at nothing — which is why a step anchored to a section that is not the active
 * tab simply vanishes instead of misfiring.
 *
 * Copy discipline: no step asserts a state the platform cannot observe. Where the honest answer is
 * "this depends on your deployment", the step says so and names the control that can tell you.
 */

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const POLICY_MANAGEMENT_TOUR_ID = 'policy-management';

export const POLICY_MANAGEMENT_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'pm-intro',
    title: 'One place for PR Policy',
    anchor: null,
    body: [
      'Start with PR scan settings to choose scans. Rule bundles and conditional policies let you refine what runs ' +
        'and what blocks a pull request. Azure DevOps and GitHub.com use these shared settings.',
    ],
  },
  {
    id: 'pm-settings',
    title: 'PR scan settings',
    anchor: 'pr-policy-tab-settings',
    body: [
      'Choose scans for an organization, workspace or repository here. Paid AI review and spending limits appear only when ' +
        'you open that option for a repository.',
    ],
  },
  {
    id: 'pm-bundles',
    title: 'Rule bundles',
    anchor: 'pr-policy-tab-bundles',
    body: [
      'Filter bundles by workspace here, then add rules and choose the scan tier for repositories, workspaces or the organization.',
    ],
  },
  {
    id: 'pm-conditional',
    title: 'Conditional policies',
    anchor: 'pr-policy-tab-conditional',
    body: [
      'Filter policies by workspace here, add conditions for pull-request results and choose what happens when they match. Preview the effective ' +
        'policy when you need to check a repository.',
    ],
  },
];

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const SCHEDULE_SCAN_TOUR_ID = 'schedule-scan';

export const SCHEDULE_SCAN_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'ss-intro',
    title: 'Scans that run without anyone asking',
    anchor: null,
    body: [
      'A schedule names a scope and a cadence. When its window arrives, Phoenix expands the scope ' +
        'to a repository list and queues a SAST scan for each one.',
      'Nothing here runs until a schedule exists. An organization with no schedules scans only when ' +
        'a pull request or a person triggers it.',
    ],
  },
  {
    id: 'ss-create',
    title: 'Scope, cadence, and the ceiling',
    anchor: 'schedule-toolbar',
    body: [
      'A schedule covers one repository, one workspace, or every repository in the organization. ' +
        'The last of those is the largest action on this page — it is labelled as such where you ' +
        'choose it.',
        'Each schedule keeps the timezone of whoever created it, and the list shows that zone ' +
        'beside the time. The maximum-repositories setting is a ceiling: if the scope expands ' +
        'past it the remainder is not scanned, and the run is recorded as truncated rather than ' +
        'quietly coming up short.',
    ],
  },
  {
    id: 'ss-list',
    title: 'What each row tells you',
    anchor: 'schedule-list',
    body: [
      'Next run is the window the sweep will act on; a disabled schedule has none. Re-enabling one ' +
        'recomputes that window, so a schedule left off for a month does not come back with a stale ' +
        'one.',
      'Every row starts collapsed. Clicking one expands its run history inside the row, and ' +
        'clicking it again closes it.',
    ],
  },
  {
    id: 'ss-runs',
    title: 'Reading a run',
    // Anchored to the LIST, not to `schedule-runs`. That element exists only while a row is
    // expanded, and since nothing expands on load a step anchored to it would be silently skipped
    // for every first-time visitor -- which is the one reader this tour exists for.
    anchor: 'schedule-list',
    body: [
      'Between windows this panel is the only thing that changes, so it says what happened rather ' +
        'than only how many repositories were queued.',
      'A run can be short for three different reasons — it hit the ceiling, its window was missed ' +
        'and deliberately skipped, or some repositories could not be queued. Each one reads ' +
        'differently here, and a skipped window is normal rather than a failure.',
    ],
  },
];
